import { auditReports } from '@mi-front/api-clients';
import { humanFileSize } from '@mi-front/core';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorIcon from '@mui/icons-material/Error';
import { Box, Chip, CircularProgress } from '@mui/material';
import { defineMessage, useIntl } from 'react-intl';

const msgPreparing = defineMessage({
  id: 'logger.streamScreen.downloadMedia.preparing',
  defaultMessage: 'Preparando archivo...',
});
const msgError = defineMessage({
  id: 'logger.streamScreen.downloadMedia.error',
  defaultMessage: 'Error al generar el archivo, intenta nuevamente',
});
const msgDownload = defineMessage({
  id: 'common.download',
  defaultMessage: 'Descargar',
});
const msgFileGeneratedSuccessfully = defineMessage({
  id: 'logger.streamScreen.downloadMedia.fileGeneratedSuccessfully',
  defaultMessage: 'Archivo generado correctamente',
});

/** Shared layout: left slot for icon/progress, right for content (vertically centered). */
const rowSx = {
  display: 'flex',
  gap: 1.5,
  typography: 'body2' as const,
};
const leftSlotSx = {
  width: 32,
  flexShrink: 0,
  display: 'flex',
  justifyContent: 'center',
};
const contentSx = { flex: 1, minWidth: 0, my: 0.25 };

/** Initial loading state: shows 0% and preparing message. */
export function DownloadMediaNotificationLoadingView() {
  const { $t } = useIntl();
  return (
    <Box sx={rowSx}>
      <Box sx={{ ...leftSlotSx, typography: 'body2', color: 'text.secondary' }}>
        <CircularProgress size={20} />
      </Box>
      <Box sx={contentSx}>{$t(msgPreparing)}</Box>
    </Box>
  );
}

/** Error state: shows error message. */
export function DownloadMediaNotificationErrorView() {
  const { $t } = useIntl();
  return (
    <Box sx={rowSx}>
      <Box sx={leftSlotSx}>
        <ErrorIcon sx={{ width: 24, height: 24, color: 'error.main' }} />
      </Box>
      <Box sx={contentSx}>{$t(msgError)}</Box>
    </Box>
  );
}

/** Job in progress: preparing message and JobProgress. */
export function DownloadMediaNotificationPendingView({ job }: { job: auditReports.PublicJobDto }) {
  const { $t } = useIntl();
  return (
    <Box sx={rowSx}>
      <Box sx={leftSlotSx}>
        <CircularProgress variant="determinate" value={job.progressPercent} size={20} />
      </Box>
      <Box sx={contentSx}>{$t(msgPreparing)}</Box>
    </Box>
  );
}

/** Job completed: success icon, message, and download chip (with optional file size). */
export function DownloadMediaNotificationCompletedView({
  onDownload,
  downloading,
  fileSizeBytes,
}: {
  onDownload: () => void;
  downloading: boolean;
  /** When present, shown next to the download label in human-readable form (e.g. "1.5 MB"). */
  fileSizeBytes?: number;
}) {
  const { $t } = useIntl();
  const downloadLabel = $t(msgDownload);
  const chipLabel =
    fileSizeBytes != null ? `${downloadLabel} (${humanFileSize(fileSizeBytes)})` : downloadLabel;

  return (
    <Box sx={rowSx}>
      <Box sx={leftSlotSx}>
        <CheckCircleIcon sx={{ width: 24, height: 24, color: 'success.main' }} />
      </Box>
      <Box
        sx={{
          ...contentSx,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          alignItems: 'flex-start',
        }}
      >
        <Box>{$t(msgFileGeneratedSuccessfully)}</Box>
        <Chip
          size="small"
          variant="outlined"
          icon={<DownloadIcon sx={{ fontSize: 12 }} />}
          label={chipLabel}
          onClick={onDownload}
          disabled={downloading}
          clickable
        />
      </Box>
    </Box>
  );
}
